<template>
  <template v-if="$route.name === 'guardians.index'">

    <GuardianFilter 
      @search="handleSearch" 
      @gender="handleGenderFilter"
      @relation="handleRelationFilter"
      @add="navigateToAdd"
    />

    <GuardianTable
      :loading="isLoading"
      :guardians="guardians"
      @delete-guardian="openDeleteDialog"
    />

    <PaginationControls
      v-if="!isLoading && pagination && pagination.totalPages > 1"
      :pagination="pagination"
      @page-changed="handlePageChange"
      @per-page-changed="handlePerPageChange"
    />

    <DeleteGuardianDialog ref="deleteDialog" @delete="handleDeleteGuardian" />

  </template>

  <router-view></router-view>
</template>
<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";

import GuardianTable from "@/components/features/guardians/GuardianTable.vue";
import GuardianFilter from "@/components/features/guardians/GuardianFilter.vue";
import PaginationControls from "@/components/common/PaginationControls.vue";
import DeleteGuardianDialog from "@/components/features/guardians/dialogs/DeleteGuardianDialog.vue";

const { t, locale } = useI18n();
const store = useStore();
const router = useRouter();
const toast = useToast();
const deleteDialog = ref(null);

/* ------------ computed ------------ */
const guardians = computed(() => store.getters["guardian/filteredGuardians"]);
const pagination = computed(
  () =>
    store.getters["guardian/getPagination"] || {
      totalPages: 0,
      currentPage: 1,
      perPage: 10,
    }
);
const isLoading = computed(() => store.getters["guardian/isLoading"]);

/* ---------- helpers: consistent error toasts ---------- */
function extractApiMessage(error, fallbackKey = "errors.generic") {
  const data = error?.response?.data;
  if (data?.message) {
    if (typeof data.message === "object" && data.message !== null) {
      const loc = (data.locale || locale.value || "en")
        .toLowerCase()
        .startsWith("ar")
        ? "ar"
        : "en";
      if (typeof data.message[loc] === "string") return data.message[loc];
    }
    if (typeof data.message === "string") return data.message;
  }
  if (error?.response?.statusText) return error.response.statusText;
  return t(fallbackKey);
}

function notifyError(error, fallbackKey = "errors.generic") {
  const s = error?.response?.status;
  if (s === 401) return toast.error(t("auth.unauthorized", "Unauthorized"));
  if (s === 403) return toast.error(t("auth.forbidden", "Forbidden"));
  return toast.error(extractApiMessage(error, fallbackKey));
}

/* ------------ methods ------------ */
const fetchGuardians = async (page = 1, perPage = 10) => {
  try {
    await store.dispatch("guardian/fetchGuardians", {
      page,
      per_page: perPage,
    });
  } catch (error) {
    notifyError(error, "guardians.index.fetchError"); // shows Forbidden on 403
    console.error("Error fetching guardians:", error);
  }
};

const handleSearch = (searchTerm) => {
  store.commit("guardian/SET_FILTERS", { search: searchTerm });
  fetchGuardians(1);
};

const handleGenderFilter = (gender) => {
  store.commit("guardian/SET_FILTERS", { gender });
  fetchGuardians(1);
};

const handleRelationFilter = (relation) => {
  store.commit("guardian/SET_FILTERS", { relation });
  fetchGuardians(1);
};

const handlePageChange = (page) => {
  fetchGuardians(page, pagination.value.perPage);
};

const handlePerPageChange = (perPage) => {
  fetchGuardians(1, perPage);
};

const navigateToAdd = () => {
  router.push({ name: "guardians.create" });
};

const openDeleteDialog = (guardian) => {
  deleteDialog.value?.open(guardian);
};

const handleDeleteGuardian = async (guardianId) => {
  try {
    await store.dispatch('guardian/deleteGuardian', guardianId);
    await fetchGuardians(pagination.value.currentPage);
    toast.success(t('guardians.index.deleteSuccess'));
  } catch (e) {
    if (e?.status === 403) {
      toast.error(t('auth.forbidden', 'Forbidden'));
    } else {
      toast.error(e?.message || t('guardians.index.deleteError'));
    }
  }
};

/* ------------ lifecycle ------------ */
onMounted(() => fetchGuardians());
</script>
